import type { AuthorizationSubject } from "@whitehaven/permissions";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { ADMIN_HOMEPAGE_CONTENT_PATH } from "./features/content/homepage-editor-route-model.ts";
import { captureFocusReturn, focusTarget } from "./focus-management";
import {
  getPortalAreaLabel,
  isNavigationItemActive,
  NAVIGATION_MEDIA_QUERY,
  publicNavigationItems,
  reduceMobileNavigation,
  resolvePortalArea,
  shouldRestoreNavigationTrigger,
  visibleNavigationItems,
  type NavigationDismissReason,
} from "./navigation-model";

interface PortalNavigationProps {
  readonly subject: AuthorizationSubject | null;
  readonly currentPath: string;
  readonly publicSiteHref?: string;
  readonly privacyHref?: string;
  readonly homepageEditorAvailable?: boolean;
}

const initialMobileState = { open: false } as const;
const navigationPanelId = "portal-primary-navigation";

export function PortalNavigation({
  subject,
  currentPath,
  publicSiteHref = "/",
  privacyHref = "/privacy",
  homepageEditorAvailable = false,
}: PortalNavigationProps) {
  const [mobileState, dispatch] = useReducer(
    reduceMobileNavigation,
    initialMobileState,
  );
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(NAVIGATION_MEDIA_QUERY).matches,
  );
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const restoreFocusRef = useRef<(() => boolean) | null>(null);
  const area = resolvePortalArea(subject);
  const protectedItems = visibleNavigationItems(subject, area).filter(
    (item) =>
      homepageEditorAvailable || item.href !== ADMIN_HOMEPAGE_CONTENT_PATH,
  );
  const items = area
    ? protectedItems
    : publicNavigationItems.map((item) =>
        item.id === "public-website"
          ? { ...item, href: publicSiteHref }
          : item.id === "privacy"
            ? { ...item, href: privacyHref }
            : item,
      );
  const areaLabel = area ? getPortalAreaLabel(subject, area) : null;
  const navigationLabel = area
    ? `${areaLabel} navigation`
    : "Portal navigation";
  const toggleLabel = areaLabel ? `${areaLabel} menu` : "Portal menu";

  const dismissNavigation = useCallback((reason: NavigationDismissReason) => {
    dispatch({ type: "dismiss", reason });

    if (shouldRestoreNavigationTrigger(reason)) {
      window.requestAnimationFrame(() => {
        restoreFocusRef.current?.();
        restoreFocusRef.current = null;
      });
    } else {
      restoreFocusRef.current = null;
    }
  }, []);

  const toggleNavigation = () => {
    if (mobileState.open) {
      dispatch({ type: "toggle" });
      restoreFocusRef.current = null;
      return;
    }

    restoreFocusRef.current = captureFocusReturn();
    dispatch({ type: "open" });
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia(NAVIGATION_MEDIA_QUERY);
    const syncBreakpoint = () => {
      setIsMobile(mediaQuery.matches);
      if (!mediaQuery.matches) {
        dismissNavigation("desktop-breakpoint");
      }
    };

    syncBreakpoint();
    mediaQuery.addEventListener("change", syncBreakpoint);
    return () => {
      mediaQuery.removeEventListener("change", syncBreakpoint);
    };
  }, [dismissNavigation]);

  useEffect(() => {
    if (!mobileState.open) return;

    const frame = window.requestAnimationFrame(() => {
      focusTarget(firstLinkRef.current, { scroll: false });
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [mobileState.open]);

  useEffect(() => {
    if (!mobileState.open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        dismissNavigation("escape");
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dismissNavigation, mobileState.open]);

  useEffect(() => {
    dismissNavigation("route-change");
  }, [currentPath, dismissNavigation]);

  useEffect(() => {
    dismissNavigation("logout");
  }, [dismissNavigation, subject?.userId]);

  return (
    <div className="portal-navigation">
      <button
        type="button"
        className="portal-navigation__toggle"
        aria-controls={navigationPanelId}
        aria-expanded={mobileState.open}
        aria-label={toggleLabel}
        onClick={toggleNavigation}
      >
        <span aria-hidden="true" className="portal-navigation__toggle-icon">
          <i />
          <i />
          <i />
        </span>
        <span>{toggleLabel}</span>
      </button>

      <nav
        id={navigationPanelId}
        className={`portal-navigation__panel${
          mobileState.open ? " portal-navigation__panel--open" : ""
        }`}
        aria-label={navigationLabel}
        hidden={isMobile && !mobileState.open}
      >
        {area && (
          <span className="portal-navigation__area" aria-hidden="true">
            {areaLabel}
          </span>
        )}
        {items.length > 0 ? (
          <ul className="portal-navigation__links">
            {items.map((item, index) => (
              <li key={item.id}>
                <a
                  ref={index === 0 ? firstLinkRef : undefined}
                  href={item.href}
                  aria-current={
                    isNavigationItemActive(item, currentPath)
                      ? "page"
                      : undefined
                  }
                  onClick={() => dismissNavigation("selection")}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="portal-navigation__empty">
            No destinations are available for this account.
          </p>
        )}
      </nav>
    </div>
  );
}
